import { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { Modal } from "../components/Modal";
import BasicForm from "../components/BasicForm";
import { Plus, Wallet } from "lucide-react";

export default function Ledger({ vendors, initialVendor, onRefresh }) {
  const [selected, setSelected] = useState(initialVendor?.name || vendors[0]?.name || "");
  const [ledger, setLedger] = useState(null);
  const [payModal, setPayModal] = useState(false);

  useEffect(() => {
    if (!selected) return;
    api.get(`/ledger/${encodeURIComponent(selected)}`).then((r) => setLedger(r.data)).catch(() => setLedger(null));
  }, [selected]);

  const recordPayment = async (data) => {
    await api.post("/payments", {
      vendor: selected,
      amount: Number(data.amount || 0),
      date: data.date,
      mode: data.mode,
      reference: data.reference,
    });
    setPayModal(false);
    const r = await api.get(`/ledger/${encodeURIComponent(selected)}`);
    setLedger(r.data);
    onRefresh?.();
  };

  return (
    <section className="panel table-panel ledger-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">PARTY LEDGER</p>
          <h3>Vendor account statement</h3>
          <p className="muted">Track purchases, payments, and outstanding balance — Vyapar-style.</p>
        </div>
        <div className="ledger-actions">
          <select data-testid="ledger-vendor-select" value={selected} onChange={(e) => setSelected(e.target.value)}>
            {vendors.map((v) => <option key={v.id || v.name}>{v.name}</option>)}
          </select>
          <button className="primary sm" data-testid="record-payment-button" onClick={() => setPayModal(true)}>
            <Plus size={14} /> Record payment
          </button>
        </div>
      </div>

      {ledger && (
        <>
          <div className="ledger-summary">
            <div className="ledger-card">
              <p className="eyebrow">OUTSTANDING</p>
              <strong className={ledger.outstanding > 0 ? "owed" : ""}>{money(ledger.outstanding)}</strong>
              <span>{ledger.outstanding > 0 ? "You owe this vendor" : ledger.outstanding < 0 ? "Advance paid" : "Fully settled"}</span>
            </div>
            <div className="ledger-card">
              <p className="eyebrow">CREDIT TERMS</p>
              <strong>{ledger.vendor.credit_terms}</strong>
              <span>{ledger.vendor.category}</span>
            </div>
            <div className="ledger-card">
              <p className="eyebrow">CONTACT</p>
              <strong>{ledger.vendor.phone || "—"}</strong>
              <span>{ledger.vendor.email || ledger.vendor.address || "—"}</span>
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th>Reference</th><th>Debit</th><th>Credit</th><th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.entries.map((e, i) => (
                  <tr key={i} data-testid={`ledger-row-${i}`}>
                    <td>{e.date}</td>
                    <td>{e.type}</td>
                    <td className="mono">{e.reference}</td>
                    <td>{e.debit ? money(e.debit) : "—"}</td>
                    <td>{e.credit ? money(e.credit) : "—"}</td>
                    <td><strong>{money(e.balance)}</strong></td>
                  </tr>
                ))}
                {ledger.entries.length === 0 && (
                  <tr><td colSpan={6} className="empty">No transactions yet with this vendor.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {payModal && (
        <Modal title="Record payment" subtitle={`PAY ${selected}`} onClose={() => setPayModal(false)}>
          <BasicForm
            submit="Record payment"
            onClose={() => setPayModal(false)}
            onSubmit={recordPayment}
            fields={[
              { key: "amount", label: "Amount (₹)", type: "number", required: true },
              { key: "date", label: "Payment date", type: "date", default: new Date().toISOString().slice(0, 10) },
              { key: "mode", label: "Mode", type: "select", options: ["Bank Transfer", "Cheque", "UPI", "Cash"] },
              { key: "reference", label: "Reference / Txn ID", full: true },
            ]}
          />
        </Modal>
      )}
    </section>
  );
}
